import { isGenetecDirectSite } from "./genetecVideoSource";

/**
 * Integrations whose cameras the connector dials as
 * `{ip}:{port}{stream_parameters}` — actuate_admin's `is_rtsp` flag on
 * `Integrations` (integration_enum.py). Keep this list in step with it.
 *
 * Edit Camera used to add the Stream Parameters column only when the camera
 * already had a path, so a camera added without one had no field to fill in
 * (ENG-2144). For these sites the column is always shown, empty or not.
 */
export const STREAM_PATH_INTEGRATIONS = [
  "rtsp",
  "hikvision",
  "lightapi",
  "alibi",
  "orchid",
  "milestone_rtsp",
  "wisenet",
  "onvif_wireguard",
];

export const usesStreamPath = (integrationTypeName?: string | null): boolean =>
  STREAM_PATH_INTEGRATIONS.includes(String(integrationTypeName ?? ""));

type SiteInfoLike = {
  integration_type_name?: string | null;
  extra_fields?: { id: string | number; value?: unknown }[] | null;
  [key: string]: unknown;
};

/**
 * Sites whose cameras need every connection field fillable even while
 * empty: the RTSP family, and a Genetec site whose Video source is `direct`
 * (the connector dials each camera unit instead of the Media Gateway).
 */
export const needsPerCameraConnectionFields = (
  siteInfo: SiteInfoLike | null | undefined
): boolean =>
  usesStreamPath(siteInfo?.integration_type_name) ||
  isGenetecDirectSite(siteInfo);

/**
 * Whether the connections table gets a Stream Parameters column: always for
 * an RTSP-family or Genetec direct site, otherwise only when the camera
 * already carries a value (the pre-ENG-2144 behaviour, kept for every other
 * integration).
 */
export const showStreamParametersColumn = (
  cameraObject: { stream_parameters?: string | null } | null | undefined,
  siteInfo: SiteInfoLike | null | undefined
): boolean =>
  Boolean(cameraObject?.stream_parameters) ||
  needsPerCameraConnectionFields(siteInfo);

/**
 * Whether the connections table gets a Username / Password column (ENG-2144).
 * An RTSP-family camera with no login of its own uses the site's credentials,
 * and Edit Camera used to hide both fields in that case, leaving no way to
 * give the camera its own login. For these sites, and for Genetec direct
 * sites (site-level camera login as the fallback), the columns are always
 * shown; every other integration keeps the value-based rule.
 */
export const showCameraCredentialColumn = (
  field: "username" | "password",
  cameraObject:
    | { username?: string | null; password?: string | null }
    | null
    | undefined,
  siteInfo: SiteInfoLike | null | undefined
): boolean =>
  Boolean(cameraObject?.[field]) || needsPerCameraConnectionFields(siteInfo);

export const SITE_CREDENTIALS_HELPER_TEXT =
  "Leave blank to use the site's credentials.";
